import styled from 'styled-components';
import { motion } from 'motion/react';
import Icon, {
	ICON_LEFT,
	ICON_SHOP_LINE,
	ICON_TROPHY_PLAY_LINE,
	ICON_DICES,
	ICON_CASINO_SEVEN,
	ICON_DEALER,
	ICON_GIFT_LINE,
	ICON_MEDAL_STAR_LINE,
	ICON_SUPPORT_LINE,
	ICON_TROPHY_LINE,
	ICON_TROPHY_STAR_LINE,
	ICON_TVBET,
	ICON_MONEY_BAG,
	ICON_WE_SPIN,
	ICON_VIRTUALS,
	ICON_SPORTSBOOK,
	ICON_FOOTBALL,
	ICON_FREE_TO_PLAY
} from '~/components/icon/index.js';
import { QueryEncodedLink as Link } from '~/utils/navigation';

const iconStyle = {
	height: '32',
	width: '32'
};

const EffectWrapper = styled(motion.span)`
	a {
		display: flex;
		flex-direction: column;
	}
`;

/*
	Icons are being used as external and internal links,
	so we need to check if the link coming in to the component
	is a href or to-prop, to decide which component to use.
*/
const Effect = props => {
	const { className, to, children, onClick, href, dataAt } = props;
	return (
		<EffectWrapper whileTap={{ scale: 0.9 }}>
			{to && !href && (
				<Link className={className} href={to} onClick={onClick} data-at={dataAt}>
					{children}
				</Link>
			)}
			{href && !to && (
				<a
					className={className}
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onClick}
					data-at={dataAt}
				>
					{children}
				</a>
			)}
			{!to && !href && onClick && (
				<button data-at={dataAt} onClick={onClick}>
					{children}
				</button>
			)}
		</EffectWrapper>
	);
};

const IconCircleWithLabel = styled(Effect)`
	width: ${props => props.width || 'inherit'};
`;

const IconCircle = styled.div`
	position: relative;
	align-items: center;
	background-color: var(--product-icon-bg, rgba(var(--primary), 1));
	border-radius: 50%;
	display: flex;
	height: 56px;
	justify-content: center;
	align-self: center;
	width: 56px;
	svg {
		height: 32px;
		width: 32px;
		fill: var(--product-icon, rgba(var(--base-02), 1));
	}
`;

const IconDescriptor = styled.label`
	color: var(--result-products-label-color, rgba(var(--base-02), 1));
	display: inline-block;
	font: var(--product-icon-text, var(--label-regular));
	margin: 8px 0 40px 0;
	align-self: center;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 102px;
	white-space: nowrap;
`;

const IconBadge = styled.div`
	position: absolute;
	display: inline;
	top: -5px;
	left: -20px;
`;

const IconWithLabel = ({ label, type, small, dataAt, badge, ...rest }) => (
	<IconCircleWithLabel dataAt={dataAt} {...rest}>
		<IconCircle small={small} className="icon-circle">
			{badge && <IconBadge>{badge}</IconBadge>}
			{(() => {
				switch (type) {
					case 'trophy':
						return <Icon type={ICON_TROPHY_LINE} {...iconStyle} />;
					case 'trophy-line':
						return <Icon type={ICON_TROPHY_LINE} {...iconStyle} />;
					case 'trophy-play-line':
						return <Icon type={ICON_TROPHY_PLAY_LINE} {...iconStyle} />;
					case 'dealer':
						return <Icon type={ICON_DEALER} {...iconStyle} />;
					case 'casino-seven':
						return <Icon type={ICON_CASINO_SEVEN} {...iconStyle} />;
					case 'dices':
						return <Icon type={ICON_DICES} {...iconStyle} />;
					case 'we-spin':
						return <Icon type={ICON_WE_SPIN} {...iconStyle} />;
					case 'shop':
						return <Icon type={ICON_SHOP_LINE} />;
					case 'campaign':
						return <Icon type={ICON_GIFT_LINE} {...iconStyle} />;
					case 'back':
						return <Icon type={ICON_LEFT} small={small} />;
					case 'support':
						return <Icon type={ICON_SUPPORT_LINE} {...iconStyle} />;
					case 'medal-line':
						return <Icon type={ICON_MEDAL_STAR_LINE} {...iconStyle} />;
					case 'trophy-star':
						return <Icon type={ICON_TROPHY_STAR_LINE} {...iconStyle} />;
					case 'highlight-games':
						return <Icon type={ICON_VIRTUALS} {...iconStyle} />;
					case 'tvbet':
						return <Icon type={ICON_TVBET} {...iconStyle} />;
					case 'jackpots':
						return <Icon type={ICON_MONEY_BAG} {...iconStyle} />;
					case 'medal-star-line':
						return <Icon type={ICON_MEDAL_STAR_LINE} {...iconStyle} />;
					case 'sportsbook':
						return <Icon type={ICON_SPORTSBOOK} {...iconStyle} />;
					case 'football':
						return <Icon type={ICON_FOOTBALL} {...iconStyle} />;
					case 'virtuals':
						return <Icon type={ICON_VIRTUALS} {...iconStyle} />;
					case 'free-to-play':
						return <Icon type={ICON_FREE_TO_PLAY} {...iconStyle} />;
					default:
						return <Icon type={ICON_TROPHY_LINE} {...iconStyle} />;
				}
			})()}
		</IconCircle>
		{label && <IconDescriptor>{label}</IconDescriptor>}
	</IconCircleWithLabel>
);

const IconBackButton = styled(IconWithLabel).attrs({
	toppos: props => props.top || `calc(var(--headerHeight) + 12px)`,
	leftpos: props => props.left || `12px`
})`
	position: fixed;
	z-index: 3;
	left: ${props => props.leftpos};
	top: ${props => props.toppos};
`;

const IconHalfCircle = styled(IconCircle)`
	background-color: var(--browse-circle-bg, rgba(var(--base-10), 1));
	height: 48px;
	width: 24px;
	margin: 0;
	svg {
		fill: var(--browse-circle-icon, rgba(var(--primary), 1));
	}
`;

const IconHalfCircleLeanLeft = styled(IconHalfCircle)`
	border-radius: 0 24px 24px 0;
`;

const IconHalfCircleLeanRight = styled(IconHalfCircle)`
	border-radius: 24px 0 0 24px;
`;

export { IconWithLabel, IconBackButton, IconHalfCircleLeanLeft, IconHalfCircleLeanRight };
